import { TrackToolService } from '@analytics/events';
import type { TrackedToolComponent } from '@analytics/models';
import type { OnInit } from '@angular/core';
import { Component, Input } from '@angular/core';
import type { RadioItem } from '@innogy/common-ui/forms';
import { postalAddressFormRenderingToContent } from '@innogy/common-ui/forms';
import { BaseEplusWizardComponent } from '@innogy/feh/shared';
import { Store } from '@ngrx/store';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-angular';
import {
  type SolarPanelsWizardHouseAcquisitionOption,
  type SolarPanelsWizardToolTrackingSteps,
  parsedSolarPanelsWizardToolTrackingConfig,
  selectSolarPanelsWizardViewModel,
  solarPanelsWizardFormSubmitAction,
  solarPanelsWizardQuestionnaireOpenModalAction,
} from '@feh/solar-panels-store';

import { SolarPanelsWizardRendering } from '../../models/solar-panels-wizard-rendering.interface';
import { initializeSolarPanelsFunnelComponent } from '../../utils/initialize-solar-panels-funnel-component';

@Component({
  selector: 'wl-solar-panels-wizard-container[rendering]',
  templateUrl: './solar-panels-wizard-container.component.html',
})
export class SolarPanelsWizardContainerComponent
  extends BaseEplusWizardComponent<SolarPanelsWizardRendering>
  implements OnInit, TrackedToolComponent<SolarPanelsWizardToolTrackingSteps>
{
  viewModel$ = this.store$.select(selectSolarPanelsWizardViewModel);

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() override rendering!: SolarPanelsWizardRendering;

  radioItems?: RadioItem<SolarPanelsWizardHouseAcquisitionOption>[];

  constructor(
    protected readonly store$: Store,
    private readonly trackToolService: TrackToolService
  ) {
    super();
  }

  get trackToolConfig() {
    const toolName = this.rendering.fields.componentName.value;
    return parsedSolarPanelsWizardToolTrackingConfig(toolName);
  }

  get electricityUsagePlaceholder(): string {
    return getFieldValue(this.rendering, 'ElectricityUsagePlaceholder', '');
  }

  get electricityUsageLabel(): string {
    return getFieldValue(this.rendering, 'ElectricityUsageLabel', '');
  }

  get electricityUsageTooltip(): string {
    return getFieldValue(this.rendering, 'ElectricityUsageTooltip', '');
  }

  get electricityEstimateButtonLabel(): string {
    return getFieldValue(this.rendering, 'ElectricityEstimateButton', '');
  }

  get postalFormContent() {
    return postalAddressFormRenderingToContent(this.rendering);
  }

  ngOnInit() {
    this.store$.dispatch(
      initializeSolarPanelsFunnelComponent(
        'wizard',
        this.rendering?.fields.FunnelSettings
      )
    );

    this.setRadioItems();
  }

  setRadioItems() {
    this.radioItems = [
      {
        label: this.rendering.fields.SuitableHouseTypeOptionLabel?.value ?? '',
        value: 'bought_with_own_roof',
      },
      {
        label:
          this.rendering.fields.UnsuitableHouseTypeOptionLabel?.value ?? '',
        value: 'other',
      },
    ];
  }

  submit(event: Event, formIsValid: boolean) {
    event.preventDefault();
    this.store$.dispatch(solarPanelsWizardFormSubmitAction());

    if (formIsValid) {
      this.trackToolService.completeTool(this.trackToolConfig);
    }
  }

  openModal() {
    this.store$.dispatch(solarPanelsWizardQuestionnaireOpenModalAction());
  }
}
